import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { isValidObjectId } from '../commonUtils/string.helper';
import clsx from 'clsx';

// Hook for chunked rendering
const useChunkedItems = (items, chunkSize = 50, delay = 50) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef(null);

  const loadChunks = useCallback((itemsToLoad) => {
    setIsLoading(true);
    setVisibleItems([]);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!itemsToLoad.length) {
      setIsLoading(false);
      return;
    }

    // Load first chunk immediately
    const firstChunk = itemsToLoad.slice(0, chunkSize);
    setVisibleItems(firstChunk);

    // Load remaining chunks progressively
    let currentIndex = chunkSize;
    
    const loadNextChunk = () => {
      if (currentIndex >= itemsToLoad.length) {
        setIsLoading(false);
        return;
      }

      const nextChunk = itemsToLoad.slice(currentIndex, currentIndex + chunkSize);
      setVisibleItems(prev => [...prev, ...nextChunk]);
      currentIndex += chunkSize;

      if (currentIndex < itemsToLoad.length) {
        timeoutRef.current = setTimeout(loadNextChunk, delay);
      } else {
        setIsLoading(false);
      }
    };

    if (currentIndex < itemsToLoad.length) {
      timeoutRef.current = setTimeout(loadNextChunk, delay);
    } else {
      setIsLoading(false);
    }
  }, [chunkSize, delay]);

  useEffect(() => {
    loadChunks(items);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [items, loadChunks]);

  return { visibleItems, isLoading };
};

// Optimized debounce hook
const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
};

const SelectListInput = React.memo(({
  type = 'map',
  list = [],
  onChange,
  selectedValue = '',
  placeholder,
  error,
  disabled,
  clearable = false,
  className,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const searchInputRef = useRef(null);

  // Memoize expensive calculations
  const isCustomEntry = useMemo(() => 
    type === 'map' &&
    typeof selectedValue === 'string' &&
    selectedValue &&
    !isValidObjectId(selectedValue) &&
    !list.some((item) => item.value === selectedValue),
    [type, selectedValue, list]
  );

  // Optimized filtering with prioritization
  const filteredAndSortedList = useMemo(() => {
    if (!Array.isArray(list) || list.length === 0) return [];
    
    // Filter availability first
    let availableItems;
    if (type === 'list') {
      availableItems = list;
    } else {
      availableItems = list.filter((item) => {
        const itemValue = item?.value || item;
        if (selectedValue && itemValue === selectedValue) return true;
        return !item?.used;
      });
    }

    // If no search term, return all available items
    if (!debouncedSearchTerm.trim()) return availableItems;
    
    const searchLower = debouncedSearchTerm.toLowerCase();
    
    // Filter and prioritize matches
    const exactMatches = [];
    const startsWithMatches = [];
    const containsMatches = [];
    
    availableItems.forEach((item) => {
      const text = type === 'list' ? item : item?.label || '';
      const textLower = text.toLowerCase();
      
      if (textLower === searchLower) {
        exactMatches.push(item);
      } else if (textLower.startsWith(searchLower)) {
        startsWithMatches.push(item);
      } else if (textLower.includes(searchLower)) {
        containsMatches.push(item);
      }
    });
    
    // Return prioritized results (exact matches first, then starts-with, then contains)
    return [...exactMatches, ...startsWithMatches, ...containsMatches];
  }, [debouncedSearchTerm, list, type, selectedValue]);

  // Use chunked rendering for large lists
  const { visibleItems, isLoading } = useChunkedItems(
    filteredAndSortedList, 
    50, // Load 50 items per chunk
    30  // 30ms delay between chunks
  );

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Memoized handlers
  const handleOnChange = useCallback((value) => {
    if (type === 'list') {
      onChange(value);
    } else if (type === 'map') {
      onChange(value);
    }
    setIsOpen(false);
  }, [type, onChange]);

  const handleClear = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('0');
    setSearchTerm('');
  }, [onChange]);

  const handleSearchInputChange = useCallback((e) => {
    e.stopPropagation();
    setSearchTerm(e.target.value);
  }, []);

  const handleOpenChange = useCallback((open) => {
    setIsOpen(open);
    if (open && searchInputRef.current) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
    }
  }, []);

  const handleSearchKeyDown = useCallback((e) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  // Get display value for the trigger
  const displayValue = useMemo(() => {
    if (!selectedValue) return '';
    
    if (type === 'list') return selectedValue;
    
    const selectedItem = list.find(item => (item?.value || item) === selectedValue);
    return selectedItem?.label || selectedValue;
  }, [selectedValue, type, list]);

  // Memoize rendered items
  const renderedItems = useMemo(() => {
    const items = [];
    
    // Add custom entry if applicable
    if (isCustomEntry && !list.some((item) => item.value === selectedValue)) {
      items.push(
        <SelectItem key="custom-entry" value={selectedValue}>
          {selectedValue}
        </SelectItem>
      );
    }

    // Add visible items
    visibleItems.forEach((item, i) => {
      const key = `${item?.value || item}_${i}`;
      const value = item?.value || item;
      const displayText = type === 'list' ? item : item?.label || '';
      const isUsed = type !== 'list' && item?.used;
      const isSelected = selectedValue && value === selectedValue;
      
      items.push(
        <SelectItem
          key={key}
          value={value}
          disabled={disabled}
          className={clsx(
            isUsed && !isSelected && 'opacity-50 text-muted-foreground cursor-not-allowed',
            isSelected && 'bg-accent text-accent-foreground'
          )}
        >
          <div className="flex items-center justify-between w-full">
            <span className="truncate">{displayText}</span>
            {isUsed && !isSelected && (
              <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                (Used)
              </span>
            )}
          </div>
        </SelectItem>
      );
    });

    return items;
  }, [isCustomEntry, selectedValue, list, visibleItems, type, disabled]);

  return (
    <div className="relative w-full">
      <Select 
        onValueChange={handleOnChange} 
        value={selectedValue || ''} 
        disabled={disabled}
        onOpenChange={handleOpenChange}
        open={isOpen}
      >
        <div className="relative">
          <SelectTrigger
            className={clsx(
              'w-full',
              className,
              error ? 'border-red-500 ring-red-500' : 'border-gray-300',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <SelectValue placeholder={placeholder}>
              {displayValue}
            </SelectValue>
          </SelectTrigger>

          {selectedValue && clearable && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              onMouseDown={(e) => e.preventDefault()}
              className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full z-20 bg-white border border-gray-200 shadow-sm"
              aria-label="Clear selection"
              title="Clear selection"
            >
              <XMarkIcon className="w-3 h-3 text-gray-500 hover:text-gray-700" />
            </button>
          )}
        </div>
        
        <SelectContent className="overflow-visible">
          <SelectGroup>
            {/* Search Input */}
            <div className="sticky top-0 z-10 bg-white pt-2 pb-2 px-2 border-b">
              <Input
                type="text"
                value={searchTerm}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                className="w-full p-2 border rounded"
                placeholder="Search..."
                disabled={disabled}
                ref={searchInputRef}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
            
            {/* Items */}
            <div className="max-h-60 overflow-auto">
              {renderedItems.length > 0 ? (
                <>
                  {renderedItems}
                  {isLoading && (
                    <div className="text-center py-2 text-muted-foreground">
                      <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                      <span className="ml-2">Loading more...</span>
                    </div>
                  )}
                </>
              ) : !isLoading ? (
                <div className="text-muted-foreground text-center py-4">
                  No match found
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                  <span className="ml-2">Loading...</span>
                </div>
              )}
            </div>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
});

SelectListInput.displayName = 'SelectListInput';

export default SelectListInput;